import {
  Stack,
  TextField,
  Autocomplete,
} from '@mui/material';
import _ from 'lodash';
import { type FC, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { useAllCategories, useMutateCreateCode, useMutateUpdateCode } from '../../api/index.js';
import { type Code, type CodeCategory } from '../../models/index.js';
import { capitalize, Events, track } from '../../utils/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';
import FormDialog from '../Forms/FormDialog.js';
import { type CodeFormValues } from '../Forms/formTypes.js';
import { codeFormSchema } from '../Forms/validations.js';

const defaultValues: CodeFormValues = {
  code: '',
  description: '',
  category: {} as CodeCategory,
  tags: [],
};

interface CodeFormProps {
  code?: Code;
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}

const CodeForm: FC<CodeFormProps> = ({ code, open, refetch, onClose }) => {
  const { data: categories = [] } = useAllCategories();
  const createMutation = useMutateCreateCode();
  const updateMutation = useMutateUpdateCode();

  const formattedData: CodeFormValues = useMemo(() => {
    if (code) {
      return {
        code: code.code,
        description: code.description,
        category: code.category,
        tags: code.tags,
      };
    }
    return defaultValues;
  }, [code]);

  return (
    <FormDialog
      open={open}
      name={'code'}
      onClose={onClose}
      data={formattedData}
      schema={codeFormSchema}
      isEditing={!!code}
      onSubmit={async (data, isEditing) => {
        if (isEditing && code) {
          await updateMutation.mutateAsync({
            id: code.id,
            code: data.code,
            description: data.description,
            categoryId: data.category?.id,
            tags: data.tags,
          });
          track(Events.CodeUpdate, {
            codeId: code?.id,
          });
        } else {
          const response = await createMutation.mutateAsync({
            code: data.code,
            description: data.description,
            categoryId: data.category?.id,
            tags: data.tags,
          });
          track(Events.CodeCreation, {
            codeId: response.id,
          });
        }
      }}
      refetch={refetch}
    >
      {(control, errors) => (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Controller
            name={'code'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.code}
                helperText={errors?.code?.message || 'ex. 41, 39, X, etc...'}
                label={'Code'}
              />
            )}
          />
          <Controller
            name={'description'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                InputLabelProps={{ required: true }}
                error={!!errors?.description}
                helperText={errors?.description?.message || 'ex. awake in chair, or eating in kitchen'}
                label={'Description'}
              />
            )}
          />
          <Controller
            name={'category'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  {...field}
                  value={!_.isEmpty(field.value) ? field.value : null}
                  onChange={(_: any, value: CodeCategory | null) => field.onChange(value)}
                  id={'category-select'}
                  options={categories}
                  isOptionEqualToValue={(option, value: CodeCategory) => option.id === value.id}
                  getOptionLabel={(category: CodeCategory) => (!_.isEmpty(category) ? category.name : '')}
                  renderInput={(params) => <TextField {...params} label={'Category'} />}
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={option.name} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
          <Controller
            name={'tags'}
            control={control}
            render={({ field }) => {
              return (
                <Autocomplete
                  value={field.value}
                  onChange={(_: any, value: string[] | null) => field.onChange(value)}
                  id={'tags-select'}
                  options={code?.tags || []}
                  freeSolo
                  isOptionEqualToValue={(option, value) => option === value}
                  getOptionLabel={(tag) => capitalize(tag)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={'Tags'}
                      helperText={
                        'Optional. Tags are used to make searching for codes easier. For example, you might type \'food\' when looking for the \'eating in kitchen\' code.'
                      }
                    />
                  )}
                  multiple
                  renderOption={({ key, ...props }, option, { inputValue }) => (
                    <AutocompleteOptions option={option} value={inputValue} key={key} {...props} />
                  )}
                />
              );
            }}
          />
        </Stack>
      )}
    </FormDialog>
  );
};

export default CodeForm;
